import { ReminderFormDto, type ReminderFormInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder.form.dto';
import { PlanningUnitFormDto, type PlanningUnitFormInterface } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-form.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { ProjectDtoKeysEnum } from '@zaoblako/shared/modules/project/enum/project-dto-keys.enum';
import { idLikeSchema } from '@zaoblako/shared/modules/serde/schemas/id-like.schema';
import { nonEmptyStringSchema } from '@zaoblako/shared/modules/serde/schemas/non-empty-string.schema';
import { ProjectCheckersEnum } from '@zaoblako/shared/modules/project/enum/project-checkers.enum';
import { entityLikeSchema, entityLikeSchemaStrict } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { SecurityBackendValidatorsEnum } from '@zaoblako/shared/modules/security/enums/security-backend-validators.enum';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import type { ChecklistTypeFindInterface } from '@zaoblako/shared/modules/project/dto/checklist-type/checklist-type-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { MasterMilestoneFindInterface } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';

import * as yup from 'yup';

/** Checklist create and update APIs request body - DTO signature */
export interface ChecklistFormInterface {
  /** Checklist unique ID (for updates only) */
  id?: number;
  /** Checklist name */
  name: string;
  /** Checklist description */
  description?: string;
  /** Checklist item start date and time */
  startAt: Date;
  /** Checklist item end date and time */
  endAt: Date;
  /** Checklist types */
  type: ChecklistTypeFindInterface;
  /** Responsible users */
  responsible: UserFindInterface[];
  /** Checklist reminder (for responsible users) */
  reminder?: ReminderFormInterface;
  /** Project checklist master milestone */
  masterMilestone?: MasterMilestoneFindInterface;
  /** Checklist repeat settings */
  planning?: PlanningUnitFormInterface;
}

/** Checklist create and update APIs request body - DTO schema */
export const ChecklistFormDto = SerdeDtoClass.create<ChecklistFormInterface>(ProjectDtoKeysEnum.CHECKLIST_FORM_DTO, {
  id: {
    optional: true,
    label: 'Номер',
    serializer: idLikeSchema,
    checkers: [ProjectCheckersEnum.CHECKLIST_ASSERT_EXISTS_BY_ID],
  },
  name: {
    label: 'Название',
    serializer: nonEmptyStringSchema,
  },
  description: {
    optional: true,
    label: 'Описание',
    serializer: yup.string(),
  },
  startAt: {
    label: 'Дата и время начала',
    serializer: yup.date(),
  },
  endAt: {
    label: 'Дата и время окончания',
    hint: 'Планируемые дата и время, реальные могу отличаться',
    serializer: yup.date(),
  },
  type: {
    label: 'Тип события',
    serializer: entityLikeSchemaStrict,
    checkers: [ProjectCheckersEnum.CHECKLIST_TYPE_ASSERT_EXISTS_BY_DTO],
  },
  responsible: {
    label: 'Ответственные',
    serializer: yup
      .array(entityLikeSchemaStrict)
      .min(1, DtoMessageEnum.EMPTY_ARRAY),
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  reminder: {
    optional: true,
    label: 'Напоминание для участников',
    nested: ReminderFormDto,
    serializer: (_, nested) => nested,
  },
  masterMilestone: {
    optional: true,
    label: 'Мастер-веха',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.MASTER_MILESTONE_ASSERT_EXISTS_BY_DTO],
  },
  planning: {
    optional: true,
    label: 'Планирование повторов события',
    nested: PlanningUnitFormDto,
    serializer: (_, nested) => nested,
  },
});
